import React, { useState, useEffect } from 'react';
import { CheckCircle, Heart, MessageCircle, Share2, ExternalLink, BookOpen, Mic, Globe, MoreHorizontal, Repeat2, Mail, ArrowRight, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_IMG = "https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/dnctg08g_1780602847089.jpg";

/* ── Inline subscribe panel (used in SocialPage) ── */
const SocialSubscribe = () => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [status, setStatus] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      const res = await fetch(`${API_URL}/api/cfq/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, first_name: firstName, source: 'social-page' }),
      });
      setStatus(res.ok ? 'success' : 'error');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="bg-navy rounded-xl p-5" data-testid="social-subscribe-panel">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 bg-gold/15 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
          <Mail size={16} className="text-gold" />
        </div>
        <div>
          <p className="text-cream font-medium text-sm leading-snug">Cultural Fluency — the mailing list</p>
          <p className="text-cream/35 text-xs mt-0.5 leading-relaxed">
            50-part email series on leading across difference. Twice-weekly to start, then weekly, then monthly.
          </p>
        </div>
      </div>

      {status === 'success' ? (
        <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-4 py-2.5" data-testid="social-subscribe-success">
          <CheckCircle size={14} className="text-emerald-400" />
          <span className="text-cream/70 text-sm">Check your inbox to confirm.</span>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2" data-testid="social-subscribe-form">
          <input
            type="text"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            placeholder="First name"
            className="w-full sm:w-28 bg-white/[0.07] border border-white/10 rounded-lg px-3 py-2 text-sm text-cream placeholder-cream/20 focus:outline-none focus:border-gold/30"
          />
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Email address"
            required
            className="flex-1 bg-white/[0.07] border border-white/10 rounded-lg px-3 py-2 text-sm text-cream placeholder-cream/20 focus:outline-none focus:border-gold/30"
            data-testid="social-subscribe-email"
          />
          <button
            type="submit"
            disabled={!email || status === 'loading'}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-40 flex-shrink-0"
            style={{ backgroundColor: '#C99316', color: '#071B3A' }}
            data-testid="social-subscribe-submit"
          >
            {status === 'loading'
              ? <Loader2 size={14} className="animate-spin" />
              : <><span>Subscribe</span><ArrowRight size={13} /></>}
          </button>
          {status === 'error' && <p className="text-red-400 text-xs mt-1 sm:col-span-full">Something went wrong — please try again.</p>}
        </form>
      )}
      <p className="text-cream/15 text-[10px] mt-2">Double opt-in. No spam. Unsubscribe any time.</p>
    </div>
  );
};

const SocialPage = () => {
  const [posts, setPosts] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/public/social-feed`).then(r => r.json()).catch(() => ({ posts: [] })),
      fetch(`${API_URL}/api/public/bio-profile`).then(r => r.json()).catch(() => null),
    ]).then(([feedData, profileData]) => {
      setPosts(feedData.posts || []);
      setProfile(profileData);
      setLoading(false);
    });
  }, []);

  const authorImg = profile?.social_avatar || profile?.profile_image || FALLBACK_IMG;
  const authorName = profile?.name ? `Dr ${profile.name}` : 'Dr Promise Monday';
  const socialTitle = profile?.social_title || 'Paediatrician \u2022 Author \u2022 Founder, Third Culture Leadership';
  const socialBio = profile?.social_bio || 'Equipping leaders and organisations to thrive across cultures with clarity, presence, and integrity. Author of "Cultural Fluency Without Compromise". Founder of Third Culture Leadership.';
  const socialWebsite = profile?.social_website || 'thirdcultureleadership.co.uk';
  const followers = profile?.social_followers || '4.8K';
  const connections = profile?.social_connections || '500+';

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="bg-cream min-h-screen" data-testid="social-page">
      {/* Profile Header */}
      <section className="relative pt-24 pb-0 overflow-hidden">
        {/* Cover banner — stays dark navy for LinkedIn-style visual hierarchy */}
        <div className="h-40 sm:h-52 relative overflow-hidden">
          <div className="absolute inset-0" style={{ backgroundColor: '#071B3A' }} />
          <div className="absolute top-0 right-0 w-[60%] h-full bg-gradient-to-l from-[#C99316]/[0.05] to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C99316]/20 to-transparent" />
        </div>

        <div className="max-w-2xl mx-auto px-5 sm:px-8 -mt-16 relative z-10">
          <div className="flex flex-col items-center mb-4 sm:flex-row sm:items-end sm:gap-4">
            <div className="relative mb-3 sm:mb-0" data-testid="profile-avatar">
              <div className="w-36 h-36 sm:w-40 sm:h-40 rounded-full border-4 border-cream overflow-hidden bg-cream-dark shadow-xl shadow-navy/20 ring-2 ring-gold/20">
                <img src={authorImg} alt={authorName} className="w-full h-full object-cover" style={{objectPosition: 'center 15%'}} />
              </div>
            </div>
            <div className="pb-2 text-center sm:text-left flex-1">
              <div className="flex items-center justify-center sm:justify-start gap-2">
                <h1 className="font-serif text-xl sm:text-2xl text-navy font-semibold">{authorName}</h1>
                <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0" data-testid="verified-badge">
                  <CheckCircle size={12} className="text-white" fill="white" stroke="rgb(59,130,246)" strokeWidth={2} />
                </div>
              </div>
              <p className="text-navy/40 text-sm">{socialTitle}</p>
            </div>
          </div>

          <p className="text-navy/55 text-sm leading-relaxed mb-4">{socialBio}</p>

          <div className="flex items-center gap-6 text-xs text-navy/30 mb-4">
            <span><span className="text-navy/70 font-semibold">{followers}</span> followers</span>
            <span><span className="text-navy/70 font-semibold">{connections}</span> connections</span>
            <span className="flex items-center gap-1"><Globe size={10} /> {socialWebsite}</span>
          </div>

          <div className="flex gap-2 pb-6 border-b border-navy/[0.08]">
            <a
              href="https://www.linkedin.com/company/third-culture-leadership"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-gold text-navy text-xs font-semibold rounded-full hover:bg-gold-light transition-all"
            >
              <ExternalLink size={12} /> Follow on LinkedIn
            </a>
            <a
              href="https://x.com/promisemonday_"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2 border border-navy/15 text-navy/50 text-xs font-medium rounded-full hover:border-gold/40 hover:text-gold transition-all"
            >
              Follow on X
            </a>
          </div>
        </div>
      </section>

      {/* Featured links */}
      <section className="max-w-2xl mx-auto px-5 sm:px-8 py-4">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {[
            { icon: BookOpen, label: 'Book', href: '/books' },
            { icon: Mic, label: 'Speaking', href: '/speaking' },
            { icon: Globe, label: 'Website', href: '/' },
          ].map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-navy/[0.08] rounded-full text-navy/40 text-xs hover:text-gold hover:border-gold/20 transition-all whitespace-nowrap shadow-sm shadow-navy/[0.04]"
            >
              <link.icon size={12} /> {link.label}
            </a>
          ))}
        </div>
      </section>

      {/* Subscribe panel */}
      <section className="max-w-2xl mx-auto px-5 sm:px-8 pb-4">
        <SocialSubscribe />
      </section>

      {/* Leadership insight — fills dead space, educational brand element */}
      <section className="max-w-2xl mx-auto px-5 sm:px-8 pb-4">
        <div className="bg-navy/[0.03] border border-gold/15 rounded-xl px-6 py-5">
          <p className="text-[10px] uppercase tracking-[0.25em] text-gold/60 font-sans mb-2">Third Culture Insight</p>
          <p className="font-serif text-navy/60 text-base leading-relaxed italic">
            "Cultural fluency is not performance. It is presence with awareness."
          </p>
        </div>
      </section>

      {/* Feed */}
      <section className="max-w-2xl mx-auto px-5 sm:px-8 pb-20 pt-3">
        {loading ? (
          <div className="py-16 text-center">
            <div className="w-6 h-6 border-2 border-gold/30 border-t-gold rounded-full animate-spin mx-auto" />
          </div>
        ) : posts.length === 0 ? (
          <div className="py-16 text-center">
            <MessageCircle size={32} className="mx-auto text-navy/15 mb-3" />
            <p className="text-navy/35 text-sm">No published posts yet.</p>
            <p className="text-navy/20 text-xs mt-1">Posts will appear here once published from the Social Hub.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map((post) => (
              <div
                key={post.id}
                className="bg-white border border-navy/[0.07] rounded-xl p-4 hover:border-gold/20 hover:shadow-sm hover:shadow-navy/[0.04] transition-all shadow-sm shadow-navy/[0.02]"
                data-testid={`social-post-${post.id}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <img src={authorImg} alt="" className="w-10 h-10 rounded-full object-cover" style={{objectPosition: 'center 15%'}} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-navy/80 text-sm font-semibold">{authorName}</span>
                      <CheckCircle size={12} className="text-blue-400" fill="rgb(96,165,250)" stroke="rgb(59,130,246)" strokeWidth={2} />
                    </div>
                    <p className="text-navy/25 text-[10px]">
                      {post.platforms?.includes('linkedin') ? 'LinkedIn' : 'X'} &bull; {formatDate(post.created_at)}
                    </p>
                  </div>
                  <button className="p-1 text-navy/20 hover:text-navy/40 transition-colors">
                    <MoreHorizontal size={16} />
                  </button>
                </div>

                <div className="text-navy/60 text-sm leading-relaxed whitespace-pre-line mb-3">{post.content}</div>

                {/* Media attachments */}
                {post.media_urls?.length > 0 && (
                  <div className={`mb-3 rounded-lg overflow-hidden ${post.media_urls.length === 1 ? '' : 'grid grid-cols-2 gap-1'}`}>
                    {post.media_urls.map((url, mi) => (
                      url.match(/\.(mp4|mov|avi|webm)/) ? (
                        <video key={mi} src={url} controls className="w-full rounded-lg" />
                      ) : (
                        <img key={mi} src={url} alt="" className="w-full object-cover rounded-lg" />
                      )
                    ))}
                  </div>
                )}

                {(post.impressions || post.likes || post.comments_count) && (
                  <div className="flex items-center gap-4 text-[10px] text-navy/20 mb-3 pt-2 border-t border-navy/[0.04]">
                    {post.impressions && <span>{post.impressions.toLocaleString()} impressions</span>}
                    {post.likes && <span>{post.likes} reactions</span>}
                    {post.comments_count && <span>{post.comments_count} comments</span>}
                  </div>
                )}

                <div className="flex items-center gap-1 pt-2 border-t border-navy/[0.04]">
                  {[
                    { icon: Heart, label: 'Like' },
                    { icon: MessageCircle, label: 'Comment' },
                    { icon: Repeat2, label: 'Repost' },
                    { icon: Share2, label: 'Share' },
                  ].map((action) => (
                    <button
                      key={action.label}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 text-navy/25 hover:text-gold hover:bg-gold/[0.04] rounded-lg text-xs transition-all"
                    >
                      <action.icon size={14} /> <span className="hidden sm:inline">{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default SocialPage;
